(() => {
  'use strict';
  if (document.body?.classList.contains('fqcareer-document')) {
    const chatSelector = 'shopify-chat, #shopify-chat, .fqtl8-help';
    const removeChat = node => {
      if (node.nodeType !== 1) return;
      if (node.matches(chatSelector)) { node.remove(); return; }
      node.querySelectorAll(chatSelector).forEach(chat => chat.remove());
    };
    removeChat(document.body);
    new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(removeChat)))
      .observe(document.body, { childList: true, subtree: true });
  }
  let turnstileLoader;
  function loadTurnstile() {
    if (window.turnstile?.render) return Promise.resolve(window.turnstile);
    if (turnstileLoader) return turnstileLoader;
    turnstileLoader = new Promise((resolve, reject) => {
      const callback = '__fqCareerTurnstileReady';
      window[callback] = () => { delete window[callback]; resolve(window.turnstile); };
      const script = document.createElement('script');
      script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${callback}`;
      script.async = true;
      script.onerror = () => { delete window[callback]; turnstileLoader = undefined; reject(new Error('turnstile')); };
      document.head.append(script);
    });
    return turnstileLoader;
  }
  function careerAnchorTarget(root, anchor) {
    const href = anchor.getAttribute('href') || '';
    const hashAt = href.indexOf('#');
    if (hashAt < 0) return null;
    let id;
    try { id = decodeURIComponent(href.slice(hashAt + 1)); } catch (_) { return null; }
    if (!id.startsWith('FQ-')) return null;
    const target = document.getElementById(id);
    return target && root.contains(target) ? target : null;
  }
  function initExperience(root) {
    const life=new AbortController(),opts={signal:life.signal};
    const reduced=matchMedia('(prefers-reduced-motion: reduce)'),fine=matchMedia('(hover: hover) and (pointer: fine)');
    const scene=root.querySelector('[data-career-scene]'),canvas=root.querySelector('[data-career-sky]'),ctx=canvas?.getContext('2d');
    const motion=root.querySelector('[data-career-motion]'),reading=root.querySelector('[data-career-reading]');
    const environments=[...root.querySelectorAll('[data-career-environment]')];
    const reveals=[...root.querySelectorAll('[data-career-reveal]')],titles=[...root.querySelectorAll('[data-career-text-reveal]')];
    const navLinks=[...root.querySelectorAll('.fqcareer__nav-links a')],chapters=navLinks.map(a=>careerAnchorTarget(root,a));
    const titleWords=new Map();
    // The fixed bar keeps its own small composited surface. Reserve precisely
    // its existing height without measuring or moving it during scrolling.
    const fixedNav=document.body.classList.contains('fqcareer-document')?root.querySelector('.fqcareer__nav'):null;
    const sizeNav=height=>{if(fixedNav)root.style.setProperty('--fq-career-nav-height',`${Math.ceil(height)}px`);};
    if(fixedNav)sizeNav(fixedNav.getBoundingClientRect().height);
    const navObserver=fixedNav&&'ResizeObserver' in window?new ResizeObserver(entries=>{
      const entry=entries[0],box=Array.isArray(entry.borderBoxSize)?entry.borderBoxSize[0]:entry.borderBoxSize;
      sizeNav(box?.blockSize||fixedNav.getBoundingClientRect().height);
    }):null;
    navObserver?.observe(fixedNav);
    life.signal.addEventListener('abort',()=>navObserver?.disconnect(),{once:true});
    let override=null,enabled=!reduced.matches,readMode=false,heroVisible=true,frame=0,skyFrame=0,last=0,phase=0,width=0,height=0,px=0,py=0,scrollIdle=0,scrolling=false;
    const clamp=(v,a=0,b=1)=>Math.min(b,Math.max(a,v));
    titles.forEach(title=>{
      const content=title.textContent.trim();title.textContent='';title.setAttribute('aria-label',content);
      const words=content.split(/\s+/).map((word,index,all)=>{const span=document.createElement('span');span.className='fqcareer__reveal-word';span.setAttribute('aria-hidden','true');span.textContent=word;title.append(span);if(index<all.length-1)title.append(document.createTextNode(' '));return span;});
      title.style.setProperty('--word-count',String(words.length));words.forEach((word,index)=>word.style.setProperty('--word-index',String(index)));
      titleWords.set(title,words);
    });
    const see=node=>{node.classList.remove('is-waiting');node.classList.add('is-seen');};
    const observer='IntersectionObserver' in window?new IntersectionObserver(entries=>entries.forEach(e=>{if(e.isIntersecting){see(e.target);observer.unobserve(e.target);}}),{threshold:.025,rootMargin:'0px 0px -10px 0px'}):null;
    reveals.forEach(node=>{if(enabled&&observer){node.classList.add('is-waiting');observer.observe(node);}else see(node);});
    function resizeSky(){if(!canvas||!ctx)return;width=canvas.clientWidth;height=canvas.clientHeight;const ratio=Math.min(devicePixelRatio||1,1.5);canvas.width=Math.round(width*ratio);canvas.height=Math.round(height*ratio);ctx.setTransform(ratio,0,0,ratio,0,0);}
    let pulses=[];
    function drawSky(){
      if(!ctx||!width||!height)return;ctx.clearRect(0,0,width,height);if(!enabled)return;
      for(let i=0;i<23;i++){const x=width*((i*.618033)%1)+Math.sin(phase*.15+i)*7,y=height*(.16+((i*.373)%1)*.53)+Math.cos(phase*.12+i)*5,a=(.08+.06*Math.sin(phase*.7+i))*Math.max(.2,1-Math.abs(x/width-(px+.5)));ctx.fillStyle=`rgba(184,211,255,${a})`;ctx.beginPath();ctx.arc(x,y,.6+(i%3)*.2,0,Math.PI*2);ctx.fill();}
      pulses=pulses.filter(pulse=>phase-pulse.t<2.5);
      pulses.forEach(pulse=>{
        const age=phase-pulse.t;
        ctx.beginPath();ctx.arc(pulse.x,pulse.y,age*65+8,0,Math.PI*2);
        ctx.lineWidth=.7;ctx.strokeStyle=`rgba(183,216,255,${Math.max(0,.19-age*.076)})`;ctx.stroke();
      });
    }
    function animate(ts){skyFrame=0;if(life.signal.aborted||!enabled||!heroVisible||document.hidden||scrolling)return;if(last&&ts-last<32){skyFrame=requestAnimationFrame(animate);return;}phase+=last?Math.min(ts-last,80)/1000:0;last=ts;drawSky();skyFrame=requestAnimationFrame(animate);}
    function skySchedule(){if(skyFrame)cancelAnimationFrame(skyFrame);skyFrame=0;last=0;if(enabled&&heroVisible&&!document.hidden&&!scrolling&&ctx)skyFrame=requestAnimationFrame(animate);else drawSky();}
    // Read every position before changing any style. Cache settled values so
    // scrolling does not repeatedly invalidate the entire page or finished titles.
    const styleValues=new WeakMap(),titleStates=new WeakMap();
    const progressBar=root.querySelector('[data-career-scroll-progress]');
    const externalHeader=document.querySelector('sticky-header');
    const preferences=document.querySelector('[data-career-preferences]');
    const endLine=root.querySelector('.fqcareer__end-line');
    const titleScenes=new Map(titles.map(title=>[title,title.closest('[data-career-scroll-scene]')]));
    function setStyle(node,name,value){
      if(!node)return;
      let values=styleValues.get(node);if(!values){values=new Map();styleValues.set(node,values);}
      if(values.get(name)===value)return;
      values.set(name,value);node.style.setProperty(name,value);
    }
    const number=value=>String(Math.round(value*10000)/10000);
    function update(){
      frame=0;if(!root.isConnected||document.hidden)return;
      const viewport=innerHeight,viewportWidth=innerWidth;
      let headerBottom=0;
      if(externalHeader&&!root.contains(externalHeader)){
        const b=externalHeader.getBoundingClientRect();if(b.top<=5&&b.bottom>0)headerBottom=b.bottom;
      }
      const rect=root.getBoundingClientRect(),heroRect=scene.getBoundingClientRect();
      const endRect=endLine?.getBoundingClientRect();
      const pickerLift=endRect&&endRect.top<viewport&&endRect.bottom>0?Math.max(viewportWidth<=600?10:18,viewport-endRect.top+16):viewportWidth<=600?10:18;
      const sceneRects=new Map();
      const titleUpdates=[];
      titles.forEach(title=>{
        if(title.closest('[hidden]'))return;
        const b=title.getBoundingClientRect(),scrollScene=titleScenes.get(title);
        let ratio=enabled?clamp((viewport*.93-b.top)/Math.max(160,viewport*.48)):1;
        if(scrollScene&&enabled&&!readMode&&viewportWidth>820&&viewport>800){
          if(!sceneRects.has(scrollScene))sceneRects.set(scrollScene,scrollScene.getBoundingClientRect());
          const r=sceneRects.get(scrollScene);ratio=clamp((viewport*.9-r.top)/Math.max(1,r.height*.85));
        }
        const state=number(ratio);
        // Offscreen headings keep their completed/initial state. Reinitialize
        // after toggling motion, reading mode, or tab visibility.
        const previous=titleStates.get(title);
        if(previous===state)return;
        if(b.top>viewport+80||b.bottom<-80){
          if(ratio!==0&&ratio!==1)return;
        }
        titleUpdates.push({title,scrollScene,ratio,state});
      });
      let active=0;
      chapters.forEach((node,i)=>{if(node&&node.getBoundingClientRect().top<headerBottom+180)active=i;});
      // Only writes below this line: no synchronous geometry reads between them.
      setStyle(preferences,'--fq-career-picker-lift',`${Math.round(pickerLift)}px`);
      setStyle(root,'--fq-nav-top',`${Math.round(headerBottom)}px`);
      setStyle(progressBar,'transform',`scaleX(${number(clamp(-rect.top/Math.max(1,rect.height-viewport)))})`);
      if(heroRect.bottom>0&&heroRect.top<viewport)setStyle(scene,'--scroll',enabled?number(clamp(-heroRect.top/Math.max(1,heroRect.height))):'0');
      titleUpdates.forEach(({title,scrollScene,ratio,state})=>{
        titleStates.set(title,state);setStyle(title,'--title-progress',state);
        if(scrollScene)setStyle(scrollScene,'--journey',state);
        const words=titleWords.get(title);
        words.forEach((word,i)=>{
          const intensity=enabled?clamp((ratio*(words.length+1.7)-i)/1.7):1;
          const lit=intensity>=.995;
          if(word.classList.contains('is-lit')!==lit)word.classList.toggle('is-lit',lit);
        });
      });
      navLinks.forEach((a,i)=>{const selected=i===active;if(a.classList.contains('is-active')!==selected)a.classList.toggle('is-active',selected);});
    }
    function schedule(){if(!frame)frame=requestAnimationFrame(update);}
    function scrollSchedule(){
      if(!scrolling){scrolling=true;root.dataset.scrolling='true';skySchedule();}
      clearTimeout(scrollIdle);
      scrollIdle=setTimeout(()=>{scrollIdle=0;scrolling=false;root.dataset.scrolling='false';skySchedule();},140);
      schedule();
    }

    function apply(){
      enabled=!readMode&&(override===null?!reduced.matches:override);root.dataset.effects=String(enabled);root.dataset.reading=String(readMode);
      motion.setAttribute('aria-pressed',String(!enabled));motion.querySelector('[data-career-motion-label]').textContent=enabled?motion.dataset.pause:motion.dataset.resume;reading.setAttribute('aria-pressed',String(readMode));
      if(!enabled){reveals.forEach(see);px=py=0;scene.style.setProperty('--px','0');scene.style.setProperty('--py','0');environments.forEach(e=>{e.style.setProperty('--scene-x','0');e.style.setProperty('--scene-y','0');});}
      resizeSky();skySchedule();schedule();
    }
    motion.hidden=false;reading.hidden=false;motion.addEventListener('click',()=>{if(readMode)readMode=false;override=!enabled;apply();},opts);reading.addEventListener('click',()=>{readMode=!readMode;apply();},opts);
    const preference=()=>{override=null;apply();};reduced.addEventListener('change',preference);
    scene.addEventListener('pointermove',e=>{if(!enabled||!fine.matches)return;const b=scene.getBoundingClientRect();px=(e.clientX-b.left)/b.width-.5;py=(e.clientY-b.top)/b.height-.5;scene.style.setProperty('--px',String(px));scene.style.setProperty('--py',String(py));},opts);
    scene.addEventListener('pointerdown',e=>{
      if(!enabled||e.target.closest('a,button,input,textarea,select'))return;
      const b=canvas?.getBoundingClientRect();if(!b||!ctx||!b.width||!b.height)return;
      pulses.push({x:(e.clientX-b.left)*width/b.width,y:(e.clientY-b.top)*height/b.height,t:phase});
      pulses=pulses.slice(-4);skySchedule();
    },opts);
    scene.addEventListener('pointerleave',()=>{px=py=0;scene.style.setProperty('--px','0');scene.style.setProperty('--py','0');},opts);
    environments.forEach(env=>{
      env.addEventListener('pointermove',e=>{if(!enabled||!fine.matches||e.target.closest('input,textarea,select'))return;const b=env.getBoundingClientRect();env.style.setProperty('--scene-x',String((e.clientX-b.left)/b.width-.5));env.style.setProperty('--scene-y',String((e.clientY-b.top)/b.height-.5));},opts);
      env.addEventListener('pointerleave',()=>{env.style.setProperty('--scene-x','0');env.style.setProperty('--scene-y','0');},opts);
    });
    const lightPercent=new Intl.NumberFormat(document.documentElement.lang||'fr',{style:'percent',maximumFractionDigits:0});
    root.querySelectorAll('[data-career-light]').forEach(input=>{
      const target=input.closest('[data-career-light-scene]'),value=input.parentElement.querySelector('[data-career-light-value]');
      if(!target||!value)return;
      function illuminate(){
        const amount=clamp(Number(input.value)/100);
        target.style.setProperty('--light-level',String(amount));
        target.style.setProperty('--background-brightness',String(.85+amount*.5));
        target.style.setProperty('--scene-light',String(.12+amount*.75));
        value.textContent=amount===0?input.dataset.lightDark:amount===1?input.dataset.lightBright:lightPercent.format(amount);
        input.setAttribute('aria-valuetext',value.textContent);
      }
      input.disabled=false;illuminate();input.addEventListener('input',illuminate,opts);
    });
    addEventListener('scroll',scrollSchedule,{...opts,passive:true});addEventListener('resize',()=>{resizeSky();schedule();skySchedule();},opts);document.addEventListener('visibilitychange',()=>{root.dataset.idle=String(document.hidden);skySchedule();},opts);
    const visibility='IntersectionObserver' in window?new IntersectionObserver(entries=>{heroVisible=entries[0].isIntersecting;skySchedule();},{threshold:0}):null;visibility?.observe(scene);
    const artObserver='IntersectionObserver' in window?new IntersectionObserver(entries=>entries.forEach(e=>e.target.classList.toggle('is-offscreen',!e.isIntersecting)),{rootMargin:'100px'}):null;environments.forEach(n=>artObserver?.observe(n));
    root.querySelectorAll('a[href*="#FQ-"]').forEach(a=>a.addEventListener('click',event=>{const target=careerAnchorTarget(root,a);if(!target)return;event.preventDefault();target.scrollIntoView({behavior:enabled?'smooth':'auto',block:'start'});history.replaceState(null,'','#'+encodeURIComponent(target.id));},opts));
    function wireTabs(group){
      const tabs=[...root.querySelectorAll(`[data-career-${group}-tab]`)],panels=[...root.querySelectorAll(`[data-career-${group}-panel]`)];
      if(!tabs.length)return;
      // Reserve the tallest panel at the current width so cover backgrounds do not recrop.
      function sizePanels(){
        const heights=panels.map(panel=>{
          const hidden=panel.hidden, original=panel.getAttribute('style');
          const width=panel.parentElement.clientWidth;
          panel.hidden=false;
          panel.style.cssText+=';position:absolute!important;visibility:hidden!important;pointer-events:none!important;width:'+width+'px!important;box-sizing:border-box!important;min-height:0!important;height:auto!important;animation:none!important;';
          const height=panel.getBoundingClientRect().height;
          panel.hidden=hidden;
          if(original===null)panel.removeAttribute('style');else panel.setAttribute('style',original);
          return height;
        });
        const height=Math.ceil(Math.max(...heights));
        panels.forEach(panel=>panel.style.setProperty('min-height',height+'px','important'));
      }
      if(group!=='manifesto')sizePanels();
      let panelWidth=panels[0].parentElement.clientWidth;
      const panelObserver='ResizeObserver' in window?new ResizeObserver(()=>{
        const width=panels[0].parentElement.clientWidth;
        if(width!==panelWidth){panelWidth=width;if(group!=='manifesto')sizePanels();}
      }):null;
      panelObserver?.observe(panels[0].parentElement);
      document.fonts?.ready.then(()=>{if(!destroyed&&group!=='manifesto')sizePanels();});
      root.addEventListener('fq:career:destroy',()=>panelObserver?.disconnect(),{once:true});
      function show(index,focus=false){tabs.forEach((tab,i)=>{tab.setAttribute('aria-selected',String(i===index));tab.tabIndex=i===index?0:-1;});panels.forEach((panel,i)=>panel.hidden=i!==index);root.dataset[group]=String(index);panels[index].closest('[data-career-environment]')?.setAttribute(`data-${group}`,String(index));if(focus)tabs[index].focus({preventScroll:true});schedule();}
      tabs.forEach((tab,i)=>{tab.addEventListener('click',()=>show(i),opts);tab.addEventListener('keydown',e=>{const delta={ArrowRight:1,ArrowDown:1,ArrowLeft:-1,ArrowUp:-1}[e.key];if(delta){e.preventDefault();show((i+delta+tabs.length)%tabs.length,true);}else if(e.key==='Home'||e.key==='End'){e.preventDefault();show(e.key==='Home'?0:tabs.length-1,true);}},opts);});root.querySelector(`[data-career-${group}-tabs]`).hidden=false;show(0);
    }
    ['manifesto','inclusion','accommodation','prompt','world'].forEach(wireTabs);
    const form=root.querySelector('[data-career-form]');
    if(form){
      const name=root.querySelector('[data-career-live-name]'),world=root.querySelector('[data-career-live-world]'),mono=root.querySelector('[data-career-monogram]'),candidate=root.querySelector('[data-career-candidate]');
      const liveEmail=root.querySelector('[data-career-live-email]'),livePhone=root.querySelector('[data-career-live-phone]'),liveCountry=root.querySelector('[data-career-live-country]');
      const countrySelect=form.elements.country;
      if(countrySelect){
        let names;try{names=new Intl.DisplayNames([document.documentElement.lang||'fr'],{type:'region'});}catch{}
        const entries=[...countrySelect.options].slice(1).map(option=>({code:option.value,name:names?.of(option.value)||option.textContent}));
        entries.sort((a,b)=>a.name.localeCompare(b.name,document.documentElement.lang||'fr'));
        const selected=countrySelect.value;countrySelect.options.length=1;entries.forEach(({code,name})=>countrySelect.add(new Option(name,code)));countrySelect.value=selected;
      }
      const defaults={name:name.textContent,world:world.textContent};
      const reviews=[...root.querySelectorAll('[data-career-review]')],documentReviews=[...root.querySelectorAll('[data-career-review-file]')];
      const write=(node,text)=>{if(node&&node.textContent!==text)node.textContent=text;};
      function reflect(){
        if(root.dataset.careerSent==='true')return;
        const n=form.elements.full_name.value.trim(),u=form.elements.universe.value,phone=form.elements.phone.value.trim(),email=form.elements.email.value.trim();
        write(name,n||defaults.name);write(world,u?form.elements.universe.selectedOptions[0].textContent:defaults.world);write(liveEmail,email);write(livePhone,phone);liveEmail.hidden=!email;livePhone.hidden=!phone;
        const country=countrySelect?.value||'';write(liveCountry,country?countrySelect.selectedOptions[0].textContent:'');if(liveCountry)liveCountry.hidden=!country;
        write(mono,n?n.split(/\s+/).slice(0,2).map(part=>Array.from(part)[0]).join('').toUpperCase():'FQ');
        if(candidate)candidate.dataset.hasName=String(!!n);
        reviews.forEach(node=>{const input=form.elements[node.dataset.careerReview];if(input)write(node,input.name==='availability'&&root.fqAvailabilitySummary?root.fqAvailabilitySummary():input instanceof HTMLSelectElement?input.selectedOptions[0].textContent:input.value||'—');});
        documentReviews.forEach(node=>write(node,form.elements[node.dataset.careerReviewFile]?.files[0]?.name||'—'));
        const validIdentity=[form.elements.full_name,form.elements.email,form.elements.phone,countrySelect].every(field=>field?.validity?.valid!==false);
        const availability=form.elements.availability?.value.trim()||'';
        const identity=!!(n&&email&&phone&&country)&&validIdentity,contribution=identity&&!!u&&!!availability,complete=contribution&&!!form.elements.letter.files.length&&form.elements.privacy_ack.checked;
        // Each form stage owns 28% of the visual; the final 16% requires confirmed sending.
        const filled=(field)=>!!(field&&(field.type==='checkbox'?field.checked:field.type==='file'?field.files.length:field.value.trim())&&field.validity?.valid!==false);
        const identityPart=[form.elements.full_name,form.elements.email,form.elements.phone,countrySelect].filter(filled).length/4;
        const furthest=Number(candidate?.dataset.furthestStep||0);
        const contributionRequired=[form.elements.universe,form.elements.availability].filter(filled).length/2;
        const contributionOptional=[form.elements.contact_preference,form.elements.portfolio,form.elements.story].filter(filled).length/3;
        const contributionPart=identity?(contribution&&furthest>=2?1:contributionRequired*.7+contributionOptional*.3):0;
        const finalRequired=[form.elements.letter,form.elements.privacy_ack].filter(filled).length/2;
        const finalOptional=[form.elements.cv,form.elements.motivation].filter(filled).length/2;
        const finalPart=contribution?(complete?1:finalRequired*.7+finalOptional*.3):0;
        const formationProgress=Math.min(.84,.28*(identityPart+contributionPart+finalPart));
        candidate?.style.setProperty('--candidate-progress',String(formationProgress));
        candidate?.style.setProperty('--wolf-solid','0');
        if(candidate){
          candidate.dataset.formation=String(complete?3:contribution?2:identity?1:0);
          const stageComplete=[identity,contribution,false];
          candidate.querySelectorAll('[data-career-candidate-stage]').forEach(button=>{
            const index=Number(button.dataset.careerCandidateStage),done=stageComplete[index];
            button.dataset.filled=String(done);
            const icon=button.querySelector('.fqcareer__candidate-step-icon');
            if(icon)icon.textContent=done?'✓':String(index+1).padStart(2,'0');
          });
          const status=candidate.querySelector('[data-career-candidate-status]');
          if(status)write(status,complete?status.dataset.ready:status.dataset.building);
        }
      }
      form.addEventListener('input',reflect,opts);form.addEventListener('change',reflect,opts);reflect();
      if(candidate){candidate.addEventListener('pointermove',e=>{if(!enabled||!fine.matches)return;const b=candidate.getBoundingClientRect();candidate.style.setProperty('--card-x',String((e.clientX-b.left)/b.width-.5));candidate.style.setProperty('--card-y',String((e.clientY-b.top)/b.height-.5));},opts);candidate.addEventListener('pointerleave',()=>{candidate.style.setProperty('--card-x','0');candidate.style.setProperty('--card-y','0');},opts);}
      // Preserve native repetition. On platforms that omit repeated keydown events,
      // provide repetition only while a printable key is actually held down.
      let held=null,delay=0,repeat=0;
      function stopRepeat(){clearTimeout(delay);clearInterval(repeat);held=null;delay=repeat=0;}
      form.addEventListener('keydown',event=>{
        const input=event.target;
        if(event.repeat){stopRepeat();return;}
        stopRepeat();
        if(event.defaultPrevented||event.isComposing||event.metaKey||event.ctrlKey||event.altKey||Array.from(event.key).length!==1||!(input instanceof HTMLTextAreaElement||input instanceof HTMLInputElement&&['text','tel','search','url'].includes(input.type)))return;
        held={input,key:event.key};
        delay=setTimeout(()=>{repeat=setInterval(()=>{
          if(!held||document.hidden||document.activeElement!==input||input.disabled||input.readOnly){stopRepeat();return;}
          const start=input.selectionStart,end=input.selectionEnd;
          if(start===null||end===null||input.maxLength>=0&&input.value.length-(end-start)+held.key.length>input.maxLength){stopRepeat();return;}
          input.setRangeText(held.key,start,end,'end');input.dispatchEvent(new InputEvent('input',{bubbles:true,inputType:'insertText',data:held.key}));
        },80);},650);
      },opts);
      document.addEventListener('keyup',stopRepeat,opts);form.addEventListener('focusout',stopRepeat,opts);form.addEventListener('compositionstart',stopRepeat,opts);addEventListener('blur',stopRepeat,opts);document.addEventListener('visibilitychange',stopRepeat,opts);life.signal.addEventListener('abort',stopRepeat,{once:true});

    }
    apply();
    root.addEventListener('fq:career:destroy',()=>{clearTimeout(scrollIdle);life.abort();observer?.disconnect();visibility?.disconnect();artObserver?.disconnect();reduced.removeEventListener('change',preference);if(frame)cancelAnimationFrame(frame);if(skyFrame)cancelAnimationFrame(skyFrame);},{once:true});
  }

  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    window.FQCareerFields?.init(root,root.lang);
    initExperience(root);
    const form = root.querySelector('[data-career-form]');
    root.querySelectorAll('[data-universe]').forEach(link => link.addEventListener('click', () => {
      if (form) {
        form.elements.universe.value = link.dataset.universe;
        form.elements.universe.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }));
    if (!form) return;
    const config = JSON.parse(root.querySelector('[data-career-config]').textContent);
    // Several free Turnstile widgets can cover the mirror domains.
    const hostname = window.location.hostname.toLowerCase();
    let matchLength = -1;
    (config.hostKeys || '').split(/\r?\n/).forEach(line => {
      const split = line.indexOf('='); if (split === -1) return;
      const host = line.slice(0, split).trim().toLowerCase();
      const key = line.slice(split + 1).trim();
      if (host && key && (hostname === host || hostname.endsWith('.' + host)) && host.length > matchLength) { config.sitekey = key; matchLength = host.length; }
    });
    const message = config.messages;
    const feedback = form.querySelector('[data-career-feedback]');
    const submit = form.querySelector('[data-career-submit]');
    const originalLabel = submit.querySelector('span').textContent;
    let widgetId, token = '', ready, submitting = false, destroyed = false;
    const panels = [...form.querySelectorAll('[data-career-panel]')];
    const stepButtons = [...form.querySelectorAll('[data-career-step]')];
    const back = form.querySelector('[data-career-back]');
    const next = form.querySelector('[data-career-next]');
    const progress = form.querySelector('[data-career-progress]');
    const finalParts = [...form.querySelectorAll('[data-career-final]')];
    let currentStep = 0;
    function showStep(index, focus = false) {
      currentStep = Math.max(0, Math.min(panels.length - 1, index));
      panels.forEach((panel, i) => { panel.hidden = i !== currentStep; });
      stepButtons.forEach((button, i) => {
        if (i === currentStep) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
        button.dataset.complete = String(i < currentStep);
      });
      const last = currentStep === panels.length - 1;
      finalParts.forEach(part => { part.hidden = !last; });
      next.hidden = last; submit.hidden = !last; back.hidden = currentStep === 0;
      progress.textContent = config.progress.replace('{current}', currentStep + 1).replace('{total}', panels.length);
      if (focus) panels[currentStep].querySelector('input, select, textarea')?.focus({preventScroll:true});
      root.querySelector('[data-career-candidate]')?.style.setProperty('--candidate-step', String(currentStep));
      const card=root.querySelector('[data-career-candidate]');if(card)card.dataset.furthestStep=String(Math.max(Number(card.dataset.furthestStep||0),currentStep));
      form.dispatchEvent(new Event('input',{bubbles:true}));
      if(focus) form.scrollIntoView({behavior: root.dataset.effects === 'true' ? 'smooth' : 'auto', block:'start'});
      if (last && root.dataset.preview !== 'true') startChallenge();
    }
    let feedbackControl;
    const showError = (text, control) => {
      feedbackControl=control;
      feedback.textContent = text;
      feedback.hidden = false;
      if (control) {
        const panel = control.closest('[data-career-panel]');
        showStep(panel ? panels.indexOf(panel) : panels.length - 1);
        control.setAttribute('aria-invalid', 'true');if(root.fqFieldsFocus)root.fqFieldsFocus(control);else control.focus();
      }
      else { feedback.setAttribute('tabindex', '-1'); feedback.focus(); }
    };
    const startChallenge = () => {
      if (currentStep !== panels.length - 1) return Promise.resolve();
      if (ready) return ready;
      ready = loadTurnstile().then(api => {
        if (destroyed || !root.isConnected) return;
        const language = config.locale.toLowerCase().split('-')[0];
        widgetId = api.render(form.querySelector('[data-career-turnstile]'), {
          sitekey: config.sitekey, theme: 'dark', size: 'flexible', action: 'career_apply',
          language: ['bg','cs','da','de','el','en','es','fi','fr','hr','hu','it','lt','nl','pl','pt','ro','sk','sl','sv'].includes(language) ? language : 'auto',
          callback: value => { token = value; },
          'expired-callback': () => { token = ''; },
          'error-callback': () => { token = ''; showError(message.token); }
        });
      }).catch(() => { ready = undefined; showError(message.token); });
      return ready;
    };
    function validatePanel(index) {
      const invalid = [...panels[index].querySelectorAll('input, select, textarea')].find(el => el.willValidate && !el.checkValidity());
      if (invalid) { showError(message.validation, invalid); return false; }
      if (index === 0 && (!form.elements.full_name.value.trim() || !form.elements.email.value.trim())) {
        showError(message.validation, form.elements.full_name); return false;
      }
      if(index===0&&!validPhone()){showError(message.phone,form.elements.phone);return false;}
      return true;
    }
    function validPhone(){const v=form.elements.phone.value.trim(),digits=v.replace(/\D/g,'');return /^[+0-9().\s-]+$/.test(v)&&digits.length>=7&&digits.length<=18;}
    function advance() {
      if (submitting) return;
      feedback.hidden = true;
      if (validatePanel(currentStep)) showStep(currentStep + 1, true);
    }
    next.addEventListener('click', advance);
    back.addEventListener('click', () => { if (!submitting) { feedback.hidden = true; showStep(currentStep - 1, true); } });
    stepButtons.forEach((button, index) => button.addEventListener('click', () => {
      if (submitting) return;
      feedback.hidden = true;
      for (let i = currentStep; i < index; i++) if (!validatePanel(i)) return;
      showStep(index, true);
    }));
    form.querySelector('[data-career-step-nav]').hidden = false;
    form.classList.add('is-stepped');
    showStep(0);
    const choice = form.querySelector('[data-career-choice]');
    function updateChoice() {
      choice.hidden = !form.elements.universe.value;
      choice.querySelector('strong').textContent = form.elements.universe.selectedOptions[0]?.textContent || '';
    }
    form.elements.universe.addEventListener('change', updateChoice);
    updateChoice();
    const characters = form.querySelector('[data-career-characters]');
    const updateCharacters = () => { characters.textContent = config.characters.replace('{count}', form.elements.motivation.value.normalize('NFC').trim().length); };
    form.elements.motivation.addEventListener('input', updateCharacters);
    updateCharacters();
    if (root.dataset.preview === 'true') { /* Preview never contacts the security or email services. */ }
    else if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) { startChallenge(); observer.disconnect(); }
      }, { rootMargin: '300px' });
      observer.observe(form);
    } else startChallenge();
    if (root.dataset.preview !== 'true') form.addEventListener('focusin', () => startChallenge(), { once: true });
    form.addEventListener('input', event => event.target.removeAttribute('aria-invalid'));
    const fileInputs=[...form.querySelectorAll('[data-career-file]')],checks=new Map(),versions=new Map(),validated=new WeakMap();
    const dialog=root.querySelector('[data-career-file-dialog]'),pdfViewer=dialog.querySelector('[data-file-view-pdf]'),textViewer=dialog.querySelector('[data-file-view-text]'),viewMessage=dialog.querySelector('[data-file-view-message]');
    let viewUrl='',viewVersion=0,viewInput=null,pdfTask=null,pdfRender=null;
    let pdfLibrary;
    const loadPdf=()=>pdfLibrary||(pdfLibrary=import(config.pdfLibrary).then(lib=>{lib.GlobalWorkerOptions.workerSrc=config.pdfWorker;return lib;}).catch(error=>{pdfLibrary=null;throw error;}));
    const readFile=file=>file.arrayBuffer?file.arrayBuffer():new Promise((resolve,reject)=>{const reader=new FileReader();reader.onload=()=>resolve(reader.result);reader.onerror=reject;reader.readAsArrayBuffer(file);});
    const fileSize=size=>{const unit=size<1024?'byte':size<1024*1024?'kilobyte':'megabyte',factor=unit==='byte'?1:unit==='kilobyte'?1024:1024*1024;return new Intl.NumberFormat(config.locale,{style:'unit',unit,unitDisplay:'short',maximumFractionDigits:1}).format(size/factor);};
    function updateUpload(input){
      const box=input.closest('.fqcareer__upload'),file=input.files[0];box.classList.toggle('has-file',!!file);box.querySelector('[data-file-row]').hidden=!file;
      box.querySelector('[data-file-name]').textContent=file?file.name:'';box.querySelector('[data-file-size]').textContent=file?fileSize(file.size):'';box.querySelector('[data-file-type]').textContent=file?file.name.split('.').pop().toUpperCase():'';
      box.querySelector('[data-file-status]').textContent=file?message.fileReady:'';
      if(file){input.removeAttribute('aria-invalid');if(feedbackControl===input){feedback.hidden=true;feedbackControl=undefined;}}
      box.querySelector('[data-preview-file]').setAttribute('aria-label',file?`${dialog.querySelector('.fqcareer__eyebrow').textContent} : ${file.name}`:dialog.querySelector('.fqcareer__eyebrow').textContent);
      form.dispatchEvent(new Event('input',{bubbles:true}));
    }
    async function validFile(input){
      const file=input.files[0];if(!file)return !input.required;
      if(!/\.(pdf|docx|doc|rtf)$/i.test(file.name)){showError(message.pdf,input);return false;}
      if(file.size>5*1024*1024||file.size===0){showError(message.size,input);return false;}
      if(validated.has(file))return true;
      try{const bytes=new Uint8Array(await readFile(file));window.FQCareerDocuments.validate(bytes,file.name);validated.set(file,bytes);return true;}
      catch{showError(message.pdf,input);return false;}
    }
    function clearView(){viewVersion++;if(pdfRender){pdfRender.cancel();pdfRender=null;}if(pdfTask){pdfTask.destroy().catch(()=>{});pdfTask=null;}pdfViewer.replaceChildren();pdfViewer.hidden=true;textViewer.hidden=true;textViewer.textContent='';if(viewUrl)URL.revokeObjectURL(viewUrl);viewUrl='';viewInput=null;}
    dialog.querySelector('[data-file-close]').addEventListener('click',()=>dialog.close());dialog.addEventListener('close',clearView);
    dialog.addEventListener('click',event=>{if(event.target===dialog){const b=dialog.getBoundingClientRect();if(event.clientX<b.left||event.clientX>b.right||event.clientY<b.top||event.clientY>b.bottom)dialog.close();}});
    async function previewFile(input){
      const file=input.files[0];if(!file)return;await checks.get(input);if(!input.files[0]||!await validFile(input))return;
      clearView();viewInput=input;const version=++viewVersion;
      dialog.querySelector('[data-file-view-title]').textContent=file.name;dialog.querySelector('[data-file-view-note]').textContent=fileSize(file.size);viewMessage.textContent=message.previewLoading;viewMessage.hidden=false;
      const ext=file.name.split('.').pop().toLowerCase(),bytes=validated.get(file);const mime={pdf:'application/pdf',doc:'application/msword',docx:'application/vnd.openxmlformats-officedocument.wordprocessingml.document',rtf:'application/rtf'}[ext];
      viewUrl=URL.createObjectURL(new Blob([bytes],{type:mime}));const open=dialog.querySelector('[data-file-view-open]');open.href=viewUrl;open.download=file.name;
      dialog.showModal();
      if(ext==='pdf'){
        try{
          const lib=await loadPdf();if(version!==viewVersion)return;
          const task=lib.getDocument({data:bytes.slice(),isEvalSupported:false,enableXfa:false,useSystemFonts:true});pdfTask=task;
          const pdf=await task.promise;if(version!==viewVersion)return;
          const navigation=document.createElement('div');navigation.className='fqcareer__pdf-navigation';
          const previous=document.createElement('button'),next=document.createElement('button'),counter=document.createElement('output');
          previous.type=next.type='button';previous.textContent='←';next.textContent='→';
          previous.setAttribute('aria-label',back.getAttribute('aria-label')||back.textContent.trim());next.setAttribute('aria-label',form.querySelector('[data-career-next]').textContent.trim());
          navigation.append(previous,counter,next);const canvas=document.createElement('canvas');pdfViewer.append(navigation,canvas);
          let pageNumber=1,renderNumber=0;
          const renderPage=async()=>{
            const current=++renderNumber;previous.disabled=next.disabled=true;counter.textContent=pageNumber+' / '+pdf.numPages;
            if(pdfRender){pdfRender.cancel();pdfRender=null;}
            const page=await pdf.getPage(pageNumber);if(version!==viewVersion||current!==renderNumber)return;
            const base=page.getViewport({scale:1}),width=Math.max(240,Math.min(dialog.clientWidth-64,1100));
            const viewport=page.getViewport({scale:width/base.width}),ratio=Math.min(window.devicePixelRatio||1,2);
            if(!Number.isFinite(viewport.height)||viewport.height*ratio>16384||viewport.width*viewport.height*ratio*ratio>16000000)throw Error('page_dimensions');
            canvas.width=Math.ceil(viewport.width*ratio);canvas.height=Math.ceil(viewport.height*ratio);canvas.style.width='100%';canvas.style.height='auto';
            pdfRender=page.render({canvasContext:canvas.getContext('2d'),viewport,transform:ratio===1?null:[ratio,0,0,ratio,0,0],background:'rgb(255,255,255)'});
            await pdfRender.promise;if(version!==viewVersion||current!==renderNumber)return;pdfRender=null;
            previous.disabled=pageNumber===1;next.disabled=pageNumber===pdf.numPages;
          };
          const change=delta=>{pageNumber+=delta;renderPage().catch(error=>{if(version===viewVersion&&error.name!=='RenderingCancelledException'){viewMessage.textContent=message.previewError;viewMessage.hidden=false;}});};
          previous.addEventListener('click',()=>change(-1));next.addEventListener('click',()=>change(1));
          pdfViewer.hidden=false;await renderPage();if(version===viewVersion)viewMessage.hidden=true;
        }catch(error){if(version===viewVersion){pdfViewer.hidden=true;viewMessage.textContent=message.previewError;viewMessage.hidden=false;}}
        return;
      }
      try{
        const result=window.FQCareerDocuments.extract(bytes,file.name);let text=result.text;
        if(result.xml){const xml=new DOMParser().parseFromString(result.xml,'application/xml');if(xml.getElementsByTagName('parsererror').length)throw Error('xml');
          const paragraphs=[...xml.getElementsByTagNameNS('*','p')];text=paragraphs.map(p=>{let t='';const walk=n=>{if(n.nodeType===1){if(n.localName==='t'){t+=n.textContent;return;}if(n.localName==='tab')t+='\t';if(n.localName==='br'||n.localName==='cr')t+='\n';for(const c of n.childNodes)walk(c);}};walk(p);return t;}).join('\n\n');}
        if(version!==viewVersion)return;if(!text?.trim())throw Error('empty');textViewer.textContent=text.slice(0,250000);textViewer.hidden=false;viewMessage.hidden=true;dialog.querySelector('[data-file-view-note]').textContent=fileSize(file.size)+' · '+message.previewText;
      }catch{if(version===viewVersion){viewMessage.textContent=message.previewError;viewMessage.hidden=false;}}
    }
    fileInputs.forEach(input=>{
      input.addEventListener('change',()=>{
        const version=(versions.get(input)||0)+1;versions.set(input,version);
        const check=(async()=>{if(input.files.length&&!await validFile(input)){if(versions.get(input)===version)input.value='';}if(versions.get(input)===version)updateUpload(input);})();checks.set(input,check);
      });
      const drop=input.closest('.fqcareer__upload');let dragDepth=0;
      drop.addEventListener('dragenter',e=>{if(e.dataTransfer?.types.includes('Files')){e.preventDefault();dragDepth++;drop.classList.add('is-dragging');}});
      drop.addEventListener('dragover',e=>{e.preventDefault();if(e.dataTransfer)e.dataTransfer.dropEffect='copy';drop.classList.add('is-dragging');});
      drop.addEventListener('dragleave',e=>{e.preventDefault();if(--dragDepth<=0){dragDepth=0;drop.classList.remove('is-dragging');}});
      drop.addEventListener('drop',e=>{e.preventDefault();dragDepth=0;drop.classList.remove('is-dragging');const files=e.dataTransfer?.files;if(!files?.length)return;if(files.length!==1){showError(message.single,input);return;}try{const transfer=new DataTransfer();transfer.items.add(files[0]);input.files=transfer.files;input.dispatchEvent(new Event('change',{bubbles:true}));}catch{showError(message.validation,input);}});
      drop.querySelector('[data-preview-file]').addEventListener('click',()=>previewFile(input));
      drop.querySelector('[data-remove-file]').addEventListener('click',()=>{versions.set(input,(versions.get(input)||0)+1);input.value='';if(viewInput===input)dialog.close();updateUpload(input);input.removeAttribute('aria-invalid');input.focus({preventScroll:true});});
      updateUpload(input);
    });
    root.addEventListener('fq:career:destroy',()=>{if(dialog.open)dialog.close();clearView();},{once:true});
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (submitting) return;
      if (currentStep < panels.length - 1) { advance(); return; }
      await Promise.all([...checks.values()]);
      if(!form.elements.letter.files.length){showError(message.letter,form.elements.letter);return;}
      feedback.hidden = true;
      form.querySelectorAll('[aria-invalid]').forEach(el => el.removeAttribute('aria-invalid'));
      const invalid = Array.from(form.elements).find(el => el.willValidate && !el.checkValidity());
      if (invalid) { showError(message.validation, invalid); return; }
      if (!form.elements.full_name.value.trim() || !form.elements.email.value.trim()) { showError(message.validation, form.elements.full_name); return; }
      if(!validPhone()){showError(message.phone,form.elements.phone);return;}
      if (!await validFile(form.elements.cv) || !await validFile(form.elements.letter)) return;
      if (root.dataset.preview === 'true') {
        form.hidden = true;
        root.querySelector('[data-career-reference]').textContent = 'FQ-JOBS-DEMONSTRATION';
        const success = root.querySelector('[data-career-success]'); success.hidden = false; success.focus();
        return;
      }
      await startChallenge();
      if (!token) { showError(message.token); return; }
      let endpoint;
      try { endpoint = new URL(config.endpoint); if (endpoint.protocol !== 'https:') throw new Error('https'); }
      catch { showError(message.error); return; }
      submitting = true;
      submit.disabled = true;
      stepButtons.forEach(button => { button.disabled = true; }); back.disabled = true;
      form.setAttribute('aria-busy', 'true');
      submit.querySelector('span').textContent = message.sending;
      const data = new FormData(form);
      data.set('cf-turnstile-response', token);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 45000);
      try {
        const response = await fetch(endpoint.href, { method: 'POST', body: data, mode: 'cors', credentials: 'omit', signal: controller.signal });
        const result = await response.json();
        if (!response.ok || result.ok !== true || !/^FQ-JOBS-[A-F0-9]{16}$/.test(result.reference || '')) {
          const messages = { closed: message.closed, rate_limit: message.rate, validation: message.validation, invalid_file: message.pdf, file_too_large: message.size, letter_required: message.letter, turnstile: message.token };
          console.warn('[Fraquane Career]', response.status, String(result.code||'response').slice(0,60), String(result.diagnostic||'').slice(0,60));
          throw Object.assign(new Error('response'), { candidateMessage: messages[result.code] || message.error });
        }
        root.dataset.careerSent='true';
        const identityCard=root.querySelector('[data-career-candidate]');
        if(identityCard){
          identityCard.dataset.formation='4';
          identityCard.style.setProperty('--candidate-progress','1');
          identityCard.style.setProperty('--wolf-solid','1');
          identityCard.querySelectorAll('[data-career-candidate-stage]').forEach(item=>{item.dataset.filled='true';const icon=item.querySelector('.fqcareer__candidate-step-icon');if(icon)icon.textContent='✓';});
          const state=identityCard.querySelector('[data-career-candidate-status]');if(state)state.textContent=state.dataset.sent;
        }
        form.reset();
        form.hidden = true;
        root.querySelector('[data-career-reference]').textContent = result.reference;
        const success = root.querySelector('[data-career-success]');
        let confirmation=success.querySelector('[data-career-confirmation]');if(!confirmation){confirmation=document.createElement('p');confirmation.dataset.careerConfirmation='';success.append(confirmation);}
        confirmation.textContent=window.FQCareerFields?.confirmationMessage(config.locale,result.candidateConfirmation===true)||'';confirmation.hidden=!confirmation.textContent;
        success.hidden = false; success.focus();
      } catch (error) { showError(error.candidateMessage || message.error); }
      finally {
        clearTimeout(timeout); submitting = false; submit.disabled = false;
        stepButtons.forEach(button => { button.disabled = false; }); back.disabled = false;
        form.removeAttribute('aria-busy'); submit.querySelector('span').textContent = originalLabel;
        token = ''; if (widgetId !== undefined && window.turnstile) window.turnstile.reset(widgetId);
      }
    });
    root.addEventListener('fq:career:destroy', () => {
      destroyed = true; if (widgetId !== undefined && window.turnstile) window.turnstile.remove(widgetId);
    }, { once: true });
  }
  function initAll(scope = document) { scope.querySelectorAll('[data-fq-career]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-fq-career]').forEach(root => root.dispatchEvent(new Event('fq:career:destroy'))));
})();

/* V13: only the identity card follows the form, within its own track. */
(function () {
  function bind() {
    document.querySelectorAll('.fqcareer__candidate-track').forEach(function(track) {
      if(track.dataset.bound)return;track.dataset.bound='true';
      var aside=track.parentElement, root=aside.closest('.fqcareer');
      var card=track.querySelector('[data-career-candidate]'), form=root.querySelector('[data-career-form]');
      var nav=root.querySelector('.fqcareer__nav'), next=aside.querySelector('.fqcareer__next');
      var frame;
      function measure(){
        cancelAnimationFrame(frame);frame=requestAnimationFrame(function(){
          var desktop=window.innerWidth>980;
          var intro=track.getBoundingClientRect().top-aside.getBoundingClientRect().top;
          var available=form&&!form.hidden?form.offsetHeight:0;
          var footer=next?next.offsetHeight+57:0;
          track.style.height=desktop?Math.max(card.offsetHeight,available-intro-footer)+'px':'';
          var top=(nav?nav.getBoundingClientRect().height+(parseFloat(getComputedStyle(nav).top)||0):76)+14;
          card.style.setProperty('--fq-career-card-top',Math.max(14,Math.min(top,window.innerHeight-card.offsetHeight-20))+'px');
        });
      }
      var observer=window.ResizeObserver?new ResizeObserver(measure):null;
      [form,card,nav,next].filter(Boolean).forEach(function(n){if(observer)observer.observe(n);});
      window.addEventListener('resize',measure,{passive:true});measure();
      root.addEventListener('fq:career:destroy',function(){if(observer)observer.disconnect();cancelAnimationFrame(frame);window.removeEventListener('resize',measure);},{once:true});
    });
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',bind);else bind();
  document.addEventListener('shopify:section:load',bind);
})();

/* V11: fixed-height messages, 500 characters, no hidden overflow. */
(function () {
  function bindTextLimits() {
    document.querySelectorAll('textarea[data-career-text-size]').forEach(function (field) {
      if (field.dataset.textSizeBound) return;
      field.dataset.textSizeBound = 'true';
      field.maxLength = 500;
      var form = field.closest('form');
      var count = form && form.querySelector('[data-career-text-count="' + field.name + '"]');
      var accepted = field.value;
      var cursor = field.selectionStart || 0;
      function update() {
        if (field.value.length > 500) field.value = field.value.slice(0, 500);
        if (field.getClientRects().length && field.scrollHeight > field.clientHeight + 1) {
          field.value = accepted;
          field.setSelectionRange(cursor, cursor);
        } else {
          accepted = field.value;
          cursor = field.selectionStart || 0;
        }
        field.scrollTop = 0;
        if (count) count.textContent = field.value.length + ' / 500';
      }
      field.addEventListener('beforeinput', function () {
        accepted = field.value;
        cursor = field.selectionStart || 0;
      });
      field.addEventListener('input', update);
      if (form) form.addEventListener('reset', function () {
        requestAnimationFrame(function () { accepted = field.value; cursor = 0; update(); });
      });
      update();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindTextLimits);
  else bindTextLimits();
  document.addEventListener('shopify:section:load', bindTextLimits);
})();
